import classNames from 'classnames';
import { toast } from 'react-toastify';
import { Position } from '../../../shared/types/protocol.ts';
import { useProtocolStore } from '../../pages/protocol-view/protocolStoreProvider.tsx';
import { normalizeFloat } from '../../utils/positionInputParse.ts';
import { Input } from '../input.tsx';
import { FormMarkerList } from './formMarkerList.tsx';


interface Props {
    positionIndex: number;
    isReadOnly: boolean;
    onMenuOpenClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onDragStart: (positionIndex: number) => void;
    onDrop: (positionIndex: number) => void;
}

export function MeasurementPosition({
    positionIndex,
    isReadOnly,
    onMenuOpenClick,
    onDragStart,
    onDrop,
}: Props) {

    const protocol = useProtocolStore(state => state.protocol);
    const setPositionField = useProtocolStore(state => state.setPositionField);
    const positionSelection = useProtocolStore(state => state.positionSelection);
    const setPositionSelection = useProtocolStore(state => state.setPositionSelection);


    function handleSelect() {
        setPositionSelection({ positionIdx: positionIndex });
    }

    function reformatOnBlur(e: React.FocusEvent<HTMLInputElement>, field: keyof Position) {
        const value = e.target.value;
        const newValue = normalizeFloat(value);
        setPositionField(positionIndex, field, newValue);
    }

    function handleSymbolChange(e: React.FocusEvent<HTMLInputElement>) {
        const value = e.target.value;
        if (value.includes('◆') || value.includes('♦')) {
            toast.error(`The diamond character is not allowed in the symbol field. Use 'Crit' checkbox instead.`);
            e.preventDefault();
            return;
        }
        setPositionField(positionIndex, 'symbol', value);
    }

    function handleGeneralToleranceChange(e: React.ChangeEvent<HTMLInputElement>) {
        if (protocol?.generalTolerance == null) {
            toast.error('Must choose general tolerance first');
            e.preventDefault();
            return;
        }

        const isChecked = e.target.checked;
        setPositionField(positionIndex, 'useGeneralTolerance', isChecked);
    }


    if (!protocol) return null;

    const checkBoxStyle: React.CSSProperties = {
        margin: '0 auto',//Remove horizontal margin to help with alignment
        pointerEvents: isReadOnly ? 'none' : 'auto'
    }

    const position = protocol.positions[positionIndex];
    const isActive = positionIndex === positionSelection?.positionIdx;
    const isAuto = position.toolType === 'CMM' || position.toolType === 'VMM';
    const rowClass = classNames({
        'highlight--selected': isActive && !isAuto,
        'automatic': !isActive && isAuto,
        'automatic--selected': isActive && isAuto,
    });

    return (
        <>
            <tr
                className={rowClass}
                draggable={!isReadOnly}
                onClick={handleSelect}
                onDragStart={() => onDragStart(positionIndex)}
                onDragOver={e => e.preventDefault()}
                onDrop={() => onDrop(positionIndex)}
            >
                <td>
                    <Input
                        type='number'
                        value={position.positionNumber}
                        onChange={(e) => { setPositionField(positionIndex, 'positionNumber', e.target.value) }}
                        onFocus={handleSelect}
                        readOnly={isReadOnly}
                        style={{ pointerEvents: isReadOnly ? 'none' : 'auto' }} //Let clicks pass through when readonly
                    />
                </td>
                <td>
                    <Input
                        type='number'
                        value={position.nominal ?? ''}
                        onChange={(e) => { setPositionField(positionIndex, 'nominal', e.target.value) }}
                        onFocus={handleSelect}
                        onBlur={(e) => reformatOnBlur(e, 'nominal')}
                        readOnly={isReadOnly}
                        style={{ pointerEvents: isReadOnly ? 'none' : 'auto' }} //Let clicks pass through when readonly
                    />
                </td>
                <td>
                    <input
                        value={position.symbol ?? ''}
                        onChange={handleSymbolChange}
                        onFocus={handleSelect}
                        readOnly={isReadOnly}
                        style={{ pointerEvents: isReadOnly ? 'none' : 'auto' }} //Let clicks pass through when readonly
                    />
                </td>
                <td>
                    <Input
                        type='number'
                        value={position.upperTolerance ?? ''}
                        onChange={(e) => { setPositionField(positionIndex, 'upperTolerance', e.target.value) }}
                        onFocus={handleSelect}
                        onBlur={(e) => reformatOnBlur(e, 'upperTolerance')}
                        readOnly={isReadOnly || position.useGeneralTolerance}
                        style={{ pointerEvents: isReadOnly ? 'none' : 'auto' }} //Let clicks pass through when readonly
                    />
                </td>
                <td>
                    <Input
                        type='number'
                        value={position.lowerTolerance ?? ''}
                        onChange={(e) => { setPositionField(positionIndex, 'lowerTolerance', e.target.value) }}
                        onFocus={handleSelect}
                        onBlur={(e) => reformatOnBlur(e, 'lowerTolerance')}
                        readOnly={isReadOnly || position.useGeneralTolerance}
                        style={{ pointerEvents: isReadOnly ? 'none' : 'auto' }} //Let clicks pass through when readonly
                    />
                </td>
                <td>
                    <label className='with-checkbox'>
                        <input
                            type='checkbox'
                            name='isCritical'
                            checked={position.isCritical ?? false}
                            onChange={(e) => { setPositionField(positionIndex, 'isCritical', e.target.checked) }}
                            onFocus={handleSelect}
                            readOnly={isReadOnly}
                            style={checkBoxStyle} //Let clicks pass through when readonly
                        />
                    </label>
                </td>
                <td>
                    <label className='with-checkbox'>
                        <input
                            type='checkbox'
                            name='useGeneralTolerance'
                            checked={position.useGeneralTolerance ?? false}
                            onChange={handleGeneralToleranceChange}
                            onFocus={handleSelect}
                            readOnly={isReadOnly}
                            style={checkBoxStyle}
                        />
                    </label>
                </td>
                <td>
                    <label className='with-checkbox'>
                        <input
                            type='checkbox'
                            name='isChamfer'
                            checked={position.isChamfer ?? false}
                            onChange={(e) => { setPositionField(positionIndex, 'isChamfer', e.target.checked) }}
                            onFocus={handleSelect}
                            readOnly={isReadOnly}
                            style={checkBoxStyle}
                        />
                    </label>
                </td>
                <td>
                    <select
                        name='toolType'
                        value={position.toolType ?? ''}
                        onChange={(e) => { setPositionField(positionIndex, 'toolType', e.target.value) }}
                        onFocus={handleSelect}
                        style={{ pointerEvents: isReadOnly ? 'none' : 'auto' }} //Let clicks pass through when readonly
                    >
                        {position.toolType == null && <option value='' />}
                        <option value='CAL'>CAL</option>
                        <option value='TG'>TG</option>
                        <option value='PG'>PG</option>
                        <option value='MM'>MM</option>
                        <option value='CMM'>CMM</option>
                        <option value='VMM'>VMM</option>
                        <option value='Other'>Other</option>
                    </select>
                </td>
                <td>
                    <button
                        className='td__menu-button'
                        onClick={onMenuOpenClick}
                        onFocus={handleSelect}>
                        &#8942;
                    </button>
                </td>
            </tr>

            {position.markers.length > 1 &&
                <FormMarkerList
                    position={position}
                    positionIdx={positionIndex}
                    positionSelection={positionSelection}
                    setPositionSelection={setPositionSelection}
                    onMenuOpenClick={onMenuOpenClick}
                />
            }
        </>
    );
}
